import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';

import { Observable, combineLatest, filter, tap, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { PaginationData, PaginationRange } from 'tiime-material';

import { BusinessUserApiService } from '@api-services/business-user-api.service';
import { CompanyApiService } from '@api-services/company-api.service';
import { SendContractType } from '@enums/send-contract-type.enum';
import { ContractApiService } from '@gi/api/services/contract-api.service';
import { ContractStatus } from '@gi/enums/contract-status.enum';
import { ContractsFilters } from '@gi/interfaces/contracts-filters';
import { Contract } from '@gi/models/contract';
import { ContractAnnexModel } from '@gi/models/contract-annex-model';
import { ContractReminder } from '@gi/models/contract-reminder';
import { Prospect } from '@gi/models/prospect';
import { DateHelper, DateInput } from '@helpers/date.helper';
import { BusinessUnit } from '@models/business-unit';
import { BusinessUser } from '@models/business-user';
import { Company } from '@models/company';
import { GIFile } from '@models/gi-file';
import { ModelOffer } from '@models/model-offer';
import { ContractsHelper } from '@modules/opportunities/contracts/contracts.helper';
import { FileSaverData, FileService } from '@services/file.service';
import { ConfirmDialogData } from '@shared-dialogs/confirm-dialog/confirm-dialog-data';
import { CreateOrUpdateContractDialogService } from '@shared-dialogs/create-or-update-contract-dialog/create-or-update-contract-dialog.service';
import { SendContractDialogService } from '@shared-dialogs/send-contract-dialog/send-contract-dialog.service';
import { ReturnedUpdateContractStatusDialogData } from '@shared-dialogs/update-contract-status-dialog/returned-update-contract-status-dialog-data';
import { UpdateContractStatusDialogData } from '@shared-dialogs/update-contract-status-dialog/update-contract-status-dialog-data';
import { UpdateContractStatusDialogService } from '@shared-dialogs/update-contract-status-dialog/update-contract-status-dialog.service';

import { FacadeBase } from './facade.base';

export type ChangeContractStatusType = { contract: Contract; status: ContractStatus };

@Injectable({
  providedIn: 'root'
})
export class ContractFacade extends FacadeBase {
  constructor(
    private readonly contractApiService: ContractApiService,
    private readonly fileService: FileService,
    private readonly sendContractDialogService: SendContractDialogService,
    private readonly updateContractStatusDialogService: UpdateContractStatusDialogService,
    private readonly createOrUpdateContractDialogService: CreateOrUpdateContractDialogService,
    private readonly businessUserApiService: BusinessUserApiService,
    private readonly companyApiService: CompanyApiService
  ) {
    super();
  }

  getContract(contractId: number): Observable<Contract> {
    return this.contractApiService.getContract(contractId);
  }

  getContracts(
    pagination: PaginationRange,
    search: string,
    sort: Sort,
    filters: ContractsFilters
  ): Observable<PaginationData<Contract>> {
    return this.contractApiService.getContracts(pagination, search, sort, ContractsHelper.toParams(filters));
  }

  getCompanyContracts(companyId?: number): Observable<Contract[]> {
    return this.contractApiService.getCompanyContracts(companyId);
  }

  createContract(contract: Contract): Observable<Contract> {
    return this.contractApiService.createContract(contract);
  }

  updateContract(contract: Contract): Observable<Contract> {
    return this.contractApiService.updateContract(contract);
  }

  updateContractNextReminderDate(contractId: number, date: DateInput): Observable<Contract> {
    return this.contractApiService.updateContractNextReminderDate(contractId, date);
  }

  getReminders(contractId: number): Observable<ContractReminder[]> {
    return this.contractApiService.getReminders(contractId);
  }

  archiveContract(contractId: number, archived: boolean): Observable<Contract> {
    return this.contractApiService.archiveContract(contractId, archived);
  }

  addContractFile(contractId: number, file: File, title: string): Observable<GIFile> {
    return this.contractApiService.addContractFile(contractId, file, title);
  }

  deleteContractFile(contractId: number, fileId: number): Observable<void> {
    return this.contractApiService.deleteContractFile(contractId, fileId);
  }

  downloadContractFile(contractId: number, fileId: number): Observable<FileSaverData> {
    return this.contractApiService.downloadContractFile(contractId, fileId).pipe(
      tap((fileSaverData: FileSaverData) => {
        this.fileService.save(fileSaverData);
        this.snackbar.openSuccess('La pièce jointe a bien été téléchargée');
      })
    );
  }

  downloadContractPdf(contractId: number): Observable<FileSaverData> {
    return this.contractApiService.downloadContractPdf(contractId).pipe(
      tap((fileSaverData: FileSaverData) => {
        this.fileService.save(fileSaverData);
        this.snackbar.openSuccess('Le contrat a bien été téléchargé');
      })
    );
  }

  openContractFileInNewTab(contractId: number, fileId: number): Observable<Blob> {
    return this.contractApiService
      .getContractFilePreview(contractId, fileId)
      .pipe(tap((file: Blob) => this.fileService.openInNewTab(file)));
  }

  updateContractStatus(
    contractId: number,
    status: ContractStatus,
    lossReason?: string,
    lossComment?: string
  ): Observable<Contract> {
    return this.contractApiService.updateContractStatus(contractId, status, lossReason, lossComment);
  }

  getModelOffers(): Observable<ModelOffer[]> {
    return this.contractApiService.getModelOffers();
  }

  getAnnexModels(): Observable<ContractAnnexModel[]> {
    return this.contractApiService.getAnnexModels();
  }

  sendExternalReminder(contractId: number): Observable<void> {
    return this.contractApiService
      .sendContract(contractId, SendContractType.reminder, true, false, false)
      .pipe(tap(() => this.snackbar.openSuccess('La relance hors Expert a bien été enregistrée sur le contrat.')));
  }

  openSendExernalReminderConfirmDialog(): Observable<boolean> {
    return this.confirmDialogService
      .open({
        data: new ConfirmDialogData(
          'Attention',
          `Une relance en dehors de l'outil Tiime Expert sera indiquée comme réalisée sur le contrat, souhaitez-vous continuer ?`,
          'Confirmer',
          'Annuler',
          null,
          'accent'
        )
      })
      .afterClosed();
  }

  openConfirmUpdateContractStatus(
    currentStatus: ContractStatus,
    nextStatus: ContractStatus,
    saveMessage: boolean
  ): Observable<ReturnedUpdateContractStatusDialogData> {
    const data: UpdateContractStatusDialogData = {
      currentStatus,
      nextStatus,
      saveMessage
    };

    return this.updateContractStatusDialogService
      .open({
        data: data
      })
      .afterClosed();
  }

  openSendContractDialog(
    contract: Contract,
    type = SendContractType.signature,
    partnerId: number
  ): Observable<boolean> {
    const obs: Observable<boolean> = ContractsHelper.canSendContractWithoutConfirmation(contract)
      ? of(true)
      : this.openConfirmBeforeSendContractDialog();

    return obs.pipe(
      switchMap(() =>
        this.sendContractDialogService
          .open({
            data: {
              contract,
              type,
              partnerId
            }
          })
          .afterClosed()
      )
    );
  }

  openConfirmBeforeSendContractDialog(): Observable<boolean> {
    const data: ConfirmDialogData = new ConfirmDialogData();
    data.confirmMessage =
      'Vous avez sélectionné plusieurs offres. Etes-vous sûr de vouloir cumuler ces offres sur un même contrat ?';
    data.confirmAction = 'Confirmer';

    return this.confirmDialogService
      .open({ data })
      .afterClosed()
      .pipe(filter((confirm: boolean) => confirm));
  }

  openCreateContractDialog(source: Company | Prospect): Observable<Contract> {
    const businessUser$ = this.getBusinessUser();
    const businessUnit$ = this.getBusinessUnit(source.businessUnit.id);
    const prospectCompany$ =
      source instanceof Prospect && source.company?.id
        ? this.companyApiService.getCompanyOverview(source.company.id)
        : of(null);
    return combineLatest([businessUser$, businessUnit$, prospectCompany$]).pipe(
      switchMap(([businessUser, businessUnit, prospectCompany]: [BusinessUser, BusinessUnit, Company]) => {
        const contract = new Contract();
        if (source instanceof Prospect) {
          contract.prospect = source;
          if (prospectCompany) {
            contract.company = prospectCompany;
          }
        } else {
          contract.company = source;
        }
        contract.businessUnit = businessUnit.archivedAt ? null : businessUnit;
        contract.brand = businessUnit.brand;
        contract.logoFileId = businessUnit.logoFileId;
        contract.salesRepresentativeUser = businessUser;
        contract.status = ContractStatus.draft;
        contract.prestations = [];
        contract.files = [];
        contract.annexes = [];
        contract.startDate = DateHelper.format(DateHelper.currentDate());

        return this.openCreateOrUpdateContractDialog(contract);
      })
    );
  }

  openUpdateContractDialog(contractId: number): Observable<Contract> {
    return this.getContract(contractId).pipe(
      switchMap((contract: Contract) => this.openCreateOrUpdateContractDialog(contract))
    );
  }

  private openCreateOrUpdateContractDialog(contract: Contract): Observable<Contract> {
    return this.createOrUpdateContractDialogService
      .open({
        data: {
          contract
        }
      })
      .afterClosed()
      .pipe(filter((createdOrUpdatedContract: Contract) => !!createdOrUpdatedContract));
  }

  private getBusinessUnit(businessUnitId: number): Observable<BusinessUnit> {
    return this.businessUserApiService.getBusinessUserPartnerBusinessUnit(businessUnitId);
  }
}
